// src/api/api.ts
import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { clearToken, setAuthTokens } from "../redux/authSlice";
import { store } from "../redux/store";
import logger from "utils/logger";

// ============================================================================
// Configuration
// ============================================================================
const API_CONFIG = {
  baseURL: "/api",
  timeout: 30000, // 30 seconds default timeout
  uploadTimeout: 300000, // 5 minutes for file uploads
  maxRetries: 2,
  retryDelay: 1000, // 1 second between retries
};

// Endpoints that should have longer timeouts
const LONG_TIMEOUT_ENDPOINTS = [
  "/recording/",
  "/patient-documents/",
  "/transcribe/",
  "/event-tables/",
  "/bulk-upload/infer-schema/",
  "/bulk-upload/start-tabular/",
  "/analytics/nl-query/",
  "/insights/nl-query/",
];

const api = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout,
  headers: { "Content-Type": "application/json" },
});

// ============================================================================
// Request Interceptor
// ============================================================================

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // Attach access token
  const state = store.getState();
  const token = state.auth.token;
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Dynamic timeout for long-running endpoints
  const url = config.url || "";
  const isLongOperation = LONG_TIMEOUT_ENDPOINTS.some((ep) => url.includes(ep));
  const isUpload = config.headers?.["Content-Type"]?.toString().includes("multipart");
  
  if (isUpload || isLongOperation) {
    config.timeout = API_CONFIG.uploadTimeout;
  }

  return config;
});

// ============================================================================
// Response Interceptor
// ============================================================================

// Token refresh queue
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else if (token) prom.resolve(token);
  });
  failedQueue = [];
};

// Check if error is retryable (network issues, timeouts, 5xx errors)
const isRetryableError = (error: AxiosError): boolean => {
  if (!error.response) {
    // Network error or timeout
    return error.code === "ECONNABORTED" || error.message.includes("Network Error");
  }
  // Retry on server errors (5xx) except 501
  const status = error.response.status;
  return status >= 500 && status !== 501;
};

// Sleep helper for retry delay
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
      _retryCount?: number;
    };

    if (!originalRequest) {
      return Promise.reject(error);
    }

    // -------------------------------------------------------------------------
    // Retry logic for transient failures
    // -------------------------------------------------------------------------
    const retryCount = originalRequest._retryCount || 0;
    if (isRetryableError(error) && retryCount < API_CONFIG.maxRetries) {
      originalRequest._retryCount = retryCount + 1;
      logger.warn(
        `[API] Retry ${originalRequest._retryCount}/${API_CONFIG.maxRetries} for ${originalRequest.url}`
      );
      await sleep(API_CONFIG.retryDelay * originalRequest._retryCount);
      return api(originalRequest);
    }

    // -------------------------------------------------------------------------
    // Handle 404
    // -------------------------------------------------------------------------
    // NOTE: We intentionally do NOT redirect to /not-found here.
    // The global interceptor cannot distinguish between a resource that truly
    // doesn't exist (e.g. navigating to /patients/999/) and a transient or
    // expected 404 from data-fetching endpoints (e.g. polling table data
    // during extraction). A hard window.location redirect would abort all
    // in-flight requests and cause repeated page reloads.
    //
    // Instead, 404 handling is done by individual callers:
    //  - usePatient: renders a "Patient Not Found" UI (PatientDetails.tsx)
    //  - useTables / useDomainTypes: catch and ignore 404 from table endpoints
    //  - deleteData: treats 404 as "already deleted" (apiHelpers.tsx)
    if (error.response?.status === 404) {
      logger.warn(`[API] 404 Not Found: ${originalRequest.url}`);
      return Promise.reject(error);
    }

    // -------------------------------------------------------------------------
    // Handle 403 Beta Restricted
    // -------------------------------------------------------------------------
    if (error.response?.status === 403) {
      const data = error.response.data as Record<string, unknown> | undefined;
      if (data?.error === "beta_restricted") {
        // Dispatch a custom event so any component (e.g. BetaGateSnackbar)
        // can display a user-friendly alert without coupling to axios.
        window.dispatchEvent(
          new CustomEvent("beta-restricted", {
            detail: {
              message:
                (data?.message as string) ||
                "This feature is available to Beta Program participants only.",
            },
          })
        );
        logger.warn("[API] Beta restricted:", originalRequest.url);
      }
    }

    // -------------------------------------------------------------------------
    // Handle 402 Payment Required (quota exceeded / feature not available)
    // -------------------------------------------------------------------------
    if (error.response?.status === 402) {
      const data = error.response.data as Record<string, unknown> | undefined;
      const errCode = data?.error as string | undefined;
      window.dispatchEvent(
        new CustomEvent("billing-gate", {
          detail: {
            error: errCode || "payment_required",
            feature: data?.feature,
            message:
              (data?.message as string) ||
              "This action requires a plan upgrade.",
            upgrade_required: data?.upgrade_required ?? true,
          },
        })
      );
      logger.warn("[API] Payment required:", originalRequest.url, errCode);
    }

    // -------------------------------------------------------------------------
    // Handle 429 Rate Limiting
    // -------------------------------------------------------------------------
    if (error.response?.status === 429) {
      const retryAfter = error.response.headers["retry-after"];
      logger.warn(`[API] Rate limited. Retry after: ${retryAfter || "unknown"}s`);
      // Could implement exponential backoff here
    }

    // -------------------------------------------------------------------------
    // Handle 401 - Token refresh
    // -------------------------------------------------------------------------
    const state = store.getState();
    const refresh = state.auth.refresh;

    if (error.response?.status === 401 && refresh && !originalRequest._retry) {
      if (isRefreshing) {
        // Queue request while refresh is in progress
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await axios.post("/api/auth/refresh/", { refresh });
        const newAccess = response.data.access;
        store.dispatch(setAuthTokens({ access: newAccess, refresh }));
        processQueue(null, newAccess);

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newAccess}`;
        }
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        store.dispatch(clearToken());
        // Redirect to landing page sign-in
        window.location.href = "/?signin=1";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;

// Export config for use in other modules
export { API_CONFIG };
